import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { api, formatApiErrorDetail } from "@/lib/api";
import { FONT_OPTIONS } from "@/lib/fonts";
import UploadField from "@/components/UploadField";
import { toast } from "sonner";

const Font = Quill.import("formats/font");
Font.whitelist = ["outfit", "hind", "tiro", "mono"];
Quill.register(Font, true);

const quillModules = {
  toolbar: [
    [{ font: ["outfit", "hind", "tiro", "mono"] }],
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const FIELDS = {
  courses: [
    { key: "title", label: "Title (EN)", type: "text", required: true },
    { key: "title_bn", label: "Title (BN)", type: "text", required: true },
    { key: "slug", label: "Slug", type: "text", required: true, hint: "url-safe-id-like-this" },
    { key: "category", label: "Category", type: "select", options: ["AI Generalist", "Design", "Video", "Copywriting", "Automations"], required: true },
    { key: "level", label: "Level", type: "select", options: ["Beginner", "Intermediate", "Advanced"], required: true },
    { key: "duration_weeks", label: "Duration (weeks)", type: "number", required: true },
    { key: "price_bdt", label: "Price (BDT)", type: "number", required: true },
    { key: "instructor", label: "Instructor", type: "text", required: true },
    { key: "cover_image", label: "Cover Image (URL or upload)", type: "url", required: true, upload: "image" },
    { key: "summary", label: "Summary", type: "textarea", required: true },
    { key: "description", label: "Description", type: "textarea", required: true },
    { key: "lessons", label: "Lessons (one per line)", type: "list" },
    { key: "coming_soon", label: "Coming Soon", type: "checkbox" },
  ],
  prompts: [
    { key: "title", label: "Title", type: "text", required: true },
    { key: "title_font", label: "Title Font", type: "select", options: FONT_OPTIONS },
    { key: "slug", label: "Slug", type: "text", required: true },
    { key: "category", label: "Category", type: "select", options: ["DesignAI", "Copywriting", "Video", "Branding"], required: true },
    { key: "cover_image", label: "Cover Image (URL or upload)", type: "url", required: true, upload: "image" },
    { key: "video_url", label: "Video (URL or upload)", type: "url", upload: "video", hint: "YouTube link, direct .mp4 URL, or upload a video — plays on the prompt page" },
    { key: "summary", label: "Summary", type: "textarea", required: true },
    { key: "prompt_text", label: "Prompt Text", type: "textarea", required: true, rows: 8 },
    { key: "tags", label: "Tags (one per line)", type: "list" },
    { key: "use_cases", label: "Use Cases (one per line)", type: "list" },
  ],
  blog: [
    { key: "title", label: "Title", type: "text", required: true },
    { key: "title_font", label: "Title Font", type: "select", options: FONT_OPTIONS },
    { key: "slug", label: "Slug", type: "text", required: true },
    { key: "author", label: "Author", type: "text" },
    { key: "read_minutes", label: "Read Minutes", type: "number" },
    { key: "cover_image", label: "Cover Image (URL or upload)", type: "url", required: true, upload: "image" },
    { key: "video_url", label: "Video (URL or upload)", type: "url", upload: "video", hint: "YouTube link, direct .mp4 URL, or upload a video — plays inside the post" },
    { key: "excerpt", label: "Excerpt", type: "textarea", required: true },
    { key: "content", label: "Content", type: "richtext", required: true, hint: "Use the toolbar to change fonts, headings, bold, lists & links" },
    { key: "tags", label: "Tags (one per line)", type: "list" },
  ],
  lessons: [
    { key: "title", label: "Title", type: "text", required: true },
    { key: "title_font", label: "Title Font", type: "select", options: FONT_OPTIONS },
    { key: "slug", label: "Slug", type: "text", required: true, hint: "url-safe-id-like-this" },
    { key: "category", label: "Category", type: "select", options: ["AI Basics", "Prompting", "Tools", "Productivity", "Design", "Video", "Career"], required: true },
    { key: "cover_image", label: "Cover Image (URL or upload)", type: "url", upload: "image", hint: "Thumbnail shown on lesson cards" },
    { key: "video_url", label: "Video (URL or upload)", type: "url", upload: "video", hint: "YouTube link, direct .mp4 URL, or upload — plays at the top of the lesson" },
    { key: "excerpt", label: "Short Description", type: "textarea", required: true },
    { key: "content", label: "Lesson Content", type: "richtext", required: true, hint: "Use the toolbar to change fonts, headings, bold, lists & links" },
  ],
  news: [
    { key: "title", label: "Headline", type: "text", required: true },
    { key: "title_font", label: "Headline Font", type: "select", options: FONT_OPTIONS },
    { key: "is_article", label: "Full article (hosted on PromptsHouseBD)", type: "checkbox", hint: "Write the full story on your site — it gets its own page, related articles and share card" },
    { key: "url", label: "Link URL", type: "url", hint: "Required for link-only items · optional source link for full articles (shown at the end)" },
    { key: "summary", label: "Summary / Standfirst", type: "textarea", showIf: "is_article", hint: "One-sentence dek shown under the headline and used in emails" },
    { key: "content", label: "Article Content", type: "richtext", showIf: "is_article" },
    { key: "category", label: "Category", type: "select", options: ["Model Update", "Tool Launch", "Industry News", "Research", "Tutorial"], required: true },
    { key: "source", label: "Source", type: "text", hint: "e.g. OpenAI, Google, Anthropic" },
    { key: "image_url", label: "Image (URL or upload)", type: "url", upload: "image", hint: "Cover / thumbnail shown with this update" },
    { key: "video_url", label: "Video (URL or upload)", type: "url", upload: "video", hint: "YouTube link, .mp4 URL, or upload — playable inline on the News page" },
    { key: "send_email", label: "Email subscribers on publish", type: "checkbox", createOnly: true, hint: "Sends this to all newsletter subscribers immediately" },
  ],
};

const DEFAULTS = {
  courses: { coming_soon: false, lessons: [] },
  prompts: { tags: [], use_cases: [], video_url: "", title_font: "display" },
  blog: { tags: [], author: "PromptsHouseBD", read_minutes: 4, video_url: "", title_font: "bn-clean" },
  lessons: { category: "AI Basics", cover_image: "", video_url: "", title_font: "bn-clean" },
  news: { category: "Model Update", source: "", image_url: "", video_url: "", title_font: "display", send_email: false, is_article: false, summary: "", content: "", url: "" },
};

const LABELS = { courses: "course", prompts: "prompt", blog: "post", news: "news item", lessons: "lesson" };

export default function AdminForm({ kind, initial, onClose, onSaved }) {
  const fields = FIELDS[kind].filter(f => !(f.createOnly && initial?.id));
  const [data, setData] = useState({ ...DEFAULTS[kind], ...(initial || {}) });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setData({ ...DEFAULTS[kind], ...(initial || {}) });
  }, [kind, initial]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = { ...data };
      fields.forEach((f) => {
        if (f.type === "number" && payload[f.key] !== undefined && payload[f.key] !== "") {
          payload[f.key] = Number(payload[f.key]);
        }
        if (f.type === "list" && typeof payload[f.key] === "string") {
          payload[f.key] = payload[f.key].split("\n").map(s => s.trim()).filter(Boolean);
        }
      });
      if (initial?.id) {
        await api.put(`/admin/${kind}/${initial.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(`/admin/${kind}`, payload);
        toast.success(kind === "news" && payload.send_email ? "Published & emailing subscribers" : "Created");
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="glass-panel max-w-2xl w-full rounded-3xl p-8 my-10 relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid={`admin-form-${kind}`}
      >
        <div className="absolute top-0 inset-x-0 h-[2px]" style={{ background: "linear-gradient(90deg, transparent, #EA580C, transparent)" }} aria-hidden="true" />
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-200 hover:text-white p-2 transition-colors" data-testid="admin-form-close">
          <X />
        </button>

        <div className="overline-label mb-2">{kind}</div>
        <h2 className="font-display text-2xl font-bold text-white mb-6">{initial?.id ? "Edit" : "New"} {LABELS[kind]}</h2>

        {err && <div className="mb-4 border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400 rounded-xl">{err}</div>}

        <form onSubmit={submit} className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
          {fields.filter(f => !f.showIf || data[f.showIf]).map((f) => (
            <div key={f.key}>
              <label className="block mb-1.5 text-xs uppercase tracking-widest text-slate-200 font-semibold">{f.label}{f.required && " *"}</label>
              {f.type === "url" && f.upload ? (
                <UploadField
                  value={data[f.key] ?? ""}
                  onChange={(v) => set(f.key, v)}
                  accept={f.upload}
                  required={f.required}
                  testId={`form-upload-${f.key}`}
                />
              ) : f.type === "textarea" ? (
                <textarea
                  required={f.required}
                  rows={f.rows || 3}
                  value={data[f.key] ?? ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="input-dark"
                />
              ) : f.type === "richtext" ? (
                <div className="quill-dark" data-testid={`form-richtext-${f.key}`}>
                  <ReactQuill
                    theme="snow"
                    value={data[f.key] ?? ""}
                    onChange={(html) => set(f.key, html)}
                    modules={quillModules}
                    placeholder="Write your content…"
                  />
                </div>
              ) : f.type === "select" ? (
                <select
                  required={f.required}
                  value={data[f.key] ?? ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="input-dark"
                  data-testid={`form-select-${f.key}`}
                >
                  {!f.options.some(o => (typeof o === "string" ? o : o.value) === (data[f.key] ?? "")) && <option value="">-- choose --</option>}
                  {f.options.map(o =>
                    typeof o === "string"
                      ? <option key={o} value={o}>{o}</option>
                      : <option key={o.value} value={o.value}>{o.label}</option>
                  )}
                </select>
              ) : f.type === "checkbox" ? (
                <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!data[f.key]}
                    onChange={(e) => set(f.key, e.target.checked)}
                    className="w-4 h-4 accent-orange-500"
                    data-testid={`form-checkbox-${f.key}`}
                  />
                  Enabled
                </label>
              ) : f.type === "list" ? (
                <textarea
                  rows={4}
                  value={Array.isArray(data[f.key]) ? data[f.key].join("\n") : (data[f.key] ?? "")}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="input-dark font-mono text-sm"
                  placeholder="one item per line"
                />
              ) : (
                <input
                  type={f.type}
                  required={f.required}
                  value={data[f.key] ?? ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="input-dark"
                />
              )}
              {f.hint && <p className="text-xs text-slate-200 mt-1">{f.hint}</p>}
            </div>
          ))}
        </form>

        <div className="flex gap-3 mt-6 pt-6 border-t border-white/10">
          <button onClick={onClose} className="btn-glass flex-1 justify-center" data-testid="admin-form-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-glow flex-1 justify-center" data-testid="admin-form-save">
            {busy ? "Saving…" : (initial?.id ? "Update" : "Create")}
          </button>
        </div>
      </div>
    </div>
  );
}
